import { useMutation, useQuery } from '@apollo/client';
import { useHandleError, useMixpanel } from 'hooks';
import React, { useState } from 'react';
import { AccountLayout, Label, Row } from 'tailwind';
import { GET_CURRENT_USER } from 'utils/apollo/user';
import { getCurrentLauchOption, isWindowsPlatform } from 'utils/helpers';
import { LaunchOption } from 'views/types';
import { Link, RadioOption } from './Desktop.style';
import GET_DOWNLOAD_URL, { UPDATE_LAUNCH_OPTION } from './data';

const requirements = [
  'Windows 10/11 64-bit;',
  'NVIDIA / AMD / Intel GPU with 6 GB VRAM or more;',
  'Intel Core i5 (11th gen or newer, H-series, 6+ cores) or AMD Ryzen 5 (5000/6000 H-series, 6 cores);',
  '16/32* GB DDR4 or newer RAM (dual-channel preferably);',
  '≥ 256 GB NVMe SSD (PCIe 3.0 or newer).',
];

const Desktop = () => {
  const [launchOption, setLaunchOption] = useState<LaunchOption>('CLOUD');

  const { mixpanelTrack } = useMixpanel();
  const [handleError, enqueueSnackbar] = useHandleError();
  
  const { loading } = useQuery(GET_CURRENT_USER, {
    onCompleted: ({ currentUser }) => {
      setLaunchOption(currentUser.appEnvironment);
    },
    onError: (e) => {
      handleError(e);
    },
  });

  const [updateLaunchOption, { loading: showLoading }] = useMutation(UPDATE_LAUNCH_OPTION, {
    onCompleted: () => {
      const currentOption = getCurrentLauchOption(launchOption === 'CLOUD');
      enqueueSnackbar(`You will now open your file via the VU.CITY ${currentOption}`, {
        variant: 'success',
      });
    },
    onError: (e) => {
      const currentOption = getCurrentLauchOption(!(launchOption === 'CLOUD'));
      handleError(e);
      setLaunchOption(currentOption.toUpperCase() as LaunchOption);
    },
  });

  const [getDownloadUrl] = useMutation(GET_DOWNLOAD_URL, {
    onError: () => {
      enqueueSnackbar('Download error, please try again', { variant: 'error' });
    },
    onCompleted: ({ createDesktopRelease }) => {
      if (createDesktopRelease)
        window.location.href = createDesktopRelease;
    },
  });

  const onDownloadDesktop = () => {
    getDownloadUrl();
    mixpanelTrack('Clicked the Download VU.CITY Desktop button');
  };

  const handleClick = (option: LaunchOption) => {
    if (option === 'DESKTOP' && !isWindowsPlatform) return;
    if (option === launchOption) return;

    mixpanelTrack(`Selecting the VU.CITY ${getCurrentLauchOption(option === 'CLOUD')} option`);
    setLaunchOption(option);
    updateLaunchOption({ variables: { environment: option } });
  };

  return (
    <AccountLayout
      gql={{ loading: loading || showLoading }}
      title='VU.CITY Desktop'
      subtitle='With Pro, access the VU.CITY 3D environment via Cloud or Desktop.'
      button={{
        text: 'Download VU.CITY Desktop',
        onClick: onDownloadDesktop,
        variant: 'contained',
      }}
    >
      <Row column1='VU.CITY 3D environment'>
        <RadioOption onClick={() => handleClick('CLOUD')}>
          <input type="radio" name='version' value='CLOUD' checked={launchOption === 'CLOUD'} />
          <label htmlFor="3D">VU.CITY Cloud <Label className='ml-1'>Default</Label></label>
        </RadioOption>
        <RadioOption
          onClick={() => handleClick('DESKTOP')}
          disabled={!isWindowsPlatform}
        >
          <input type="radio"
            name='version'
            value='DESKTOP'
            checked={launchOption === 'DESKTOP'}
            disabled={!isWindowsPlatform}
          />
          <label htmlFor="desktop">VU.CITY Desktop</label>
        </RadioOption>
      </Row>

      <Row column1='VU.CITY Desktop requirements'>
        You can find our minimum and recommended options <Link
          target='_blank'
          rel='noopener noreferer'
          href='https://kb.vu.city/vucity-3d/selecting-your-environment/desktop-system-requirements'
        >here</Link>.
        For the smoothest experience, the ideal spec are as follows:

        <ul className='pl-3 my-2 leading-tight list-disc'>
          {requirements.map((req) => (
            <li key={req} dangerouslySetInnerHTML={{ __html: req }} />
          ))}
        </ul>

        We do not currently support macOS for VU.CITY Desktop.
      </Row>

      <Row column1='Technical information'>
        For more information on technical requirements, please visit <Link
          target='_blank'
          rel='noopener noreferer'
          href="https://kb.vu.city/home/install-and-setup/"
        >Knowledge Base</Link>.
      </Row>
    </AccountLayout>
  );
};

export default Desktop;
